import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Users, Trash2, Shield, UserCog } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleColors = {
  admin: 'bg-primary/20 text-primary border-primary/30',
  manager: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  viewer: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

const roleLabels = {
  admin: 'Amministratore',
  manager: 'Manager',
  viewer: 'Visualizzatore'
};

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'viewer'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }

    try {
      await axios.post(`${API}/users/invite`, formData);
      toast.success('Utente invitato con successo');
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success('Ruolo aggiornato');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Eliminare l'utente ${user.name}?`)) return;

    try {
      await axios.delete(`${API}/users/${user.id}`);
      toast.success('Utente eliminato');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-6 lg:p-8" data-testid="users-page-restricted">
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Shield size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-foreground font-medium">Accesso limitato</p>
            <p className="text-muted-foreground text-sm mt-1">
              Solo gli amministratori possono gestire gli utenti
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="users-loading">
        <Skeleton className="h-10 w-64" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Gestione Utenti</h1>
          <p className="text-muted-foreground mt-1">{users.length} utenti nell'azienda</p>
        </div>
        <Button 
          onClick={handleOpenDialog}
          className="bg-primary text-primary-foreground hover:bg-primary/90 btn-primary-glow"
          data-testid="add-user-btn"
        >
          <Plus size={18} className="mr-2" />
          Invita Utente
        </Button>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {users.map(user => (
          <Card 
            key={user.id} 
            className={`bg-card border-border ${user.id === currentUser?.id ? 'ring-1 ring-primary/30' : ''}`}
            data-testid={`user-card-${user.id}`}
          >
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-primary font-heading font-bold text-lg">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">{user.name}</p>
                      {user.id === currentUser?.id && (
                        <Badge variant="outline" className="text-xs">Tu</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {user.id !== currentUser?.id ? (
                    <>
                      <Select 
                        value={user.role} 
                        onValueChange={(v) => handleUpdateRole(user.id, v)}
                      >
                        <SelectTrigger className="w-40 bg-background" data-testid={`role-select-${user.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Amministratore</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="viewer">Visualizzatore</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(user)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </>
                  ) : (
                    <Badge variant="outline" className={roleColors[user.role]}>
                      {roleLabels[user.role]}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Role Legend */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <UserCog size={20} />
            Ruoli e Permessi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline" className={roleColors.admin}>Admin</Badge>
              <p className="text-sm text-muted-foreground">
                Accesso completo: gestione utenti, eliminazione movimenti e conti, tutte le operazioni
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className={roleColors.manager}>Manager</Badge>
              <p className="text-sm text-muted-foreground">
                Creazione e modifica conti e movimenti, visualizzazione report
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Badge variant="outline" className={roleColors.viewer}>Viewer</Badge>
              <p className="text-sm text-muted-foreground">
                Solo visualizzazione: dashboard, report, chat AI
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Invita Utente</DialogTitle>
            <DialogDescription>
              Aggiungi un nuovo utente all'azienda
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome completo</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Mario Rossi"
                className="bg-background"
                data-testid="invite-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="mario@azienda.it"
                className="bg-background"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password temporanea</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                className="bg-background"
                data-testid="invite-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Ruolo</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData({...formData, role: v})}
              >
                <SelectTrigger className="bg-background" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Amministratore</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="viewer">Visualizzatore</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="send-invite-btn"
            >
              Invita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
